/**
 * Generated by Gemini 2.5 Pro
 * 
 * @remarks
 * 基于 Reingold-Tilford 算法思想，实现的多叉树节点布局算法
 * 它考虑了两种布局方向（水平和垂直）以及可变节点尺寸
 * 
 * 核心思想:

 * 1. 两次遍历 (Two Passes):
 * 第一次遍历 (Bottom-up): 
 * 从叶节点向上计算每个节点的初步相对位置 (prelim) 和一个修正值 (mod)
 * prelim 是节点相对于其父节点的位置（在主轴上，水平布局是 y，垂直布局是 x）
 * mod 是为了避免子树与其兄弟节点的子树重叠而需要累积的偏移量。此过程还会计算子树的轮廓，以检测和解决冲突
 * 
 * 第二次遍历 (Top-down): 
 * 从根节点向下计算每个节点的最终绝对坐标 (x, y)
 * 最终坐标结合了 prelim、累积的 mod 值以及父节点的最终位置和层级间距
 * 
 * 2. 处理不同方向:
 * 通过 isHorizontal 标志切换计算逻辑
 * 水平布局 (Left-to-Right): x 由层级决定，y 由 prelim 和 mod 计算。节点 height 用于计算同级间距，width 用于计算层级间距
 * 垂直布局 (Top-to-Bottom): y 由层级决定，x 由 prelim 和 mod 计算。节点 width 用于计算同级间距，height 用于计算层级间距
 * 
 * 3. 处理不同尺寸:
 * 在计算节点间距（同级间距 siblingSeparation 和层级间距 levelSeparation）时，
 * 会考虑节点自身的 width 和 height。间距通常指节点边界之间的距离
 */

import { traverseTreeRecursiveAdvanced } from "@/map/utils/TreeUtils";
import { ITreeNodeForRT } from "../interfaces/ITreeNodeForRT";
import { AlignOptions, calculateAlignmentOffset } from "./Alignment";

// 布局选项接口
interface LayoutOptions {
    isHorizontal: boolean; // true: 从左到右, false: 从上到下
    siblingSeparation: number; // 同级节点中心间的最小距离
    levelSeparation: number;   // 层级间的最小距离 (边界到边界)
    offset?: { x: number; y: number }; // 初始偏移
    align?: AlignOptions;
    layered?: boolean; // 是否分层布局
}

// --- 核心布局函数 ---

/**
 * 计算树的布局
 * @param rootNode 树的根节点
 * @param options 布局选项
 * @returns 修改了 x, y 坐标的根节点
 */
export function layoutTree(rootNode: ITreeNodeForRT, options: LayoutOptions): ITreeNodeForRT {

    // 1. 初始化节点，添加内部属性和父引用
    initializeNodes(rootNode, null, 0);

    // 2. 第一次遍历 (Bottom-up) - 计算初步相对位置 (_prelim) 和修正值 (_mod)
    firstWalk(rootNode, options);

    // 3. 如果是分层布局，预计算层级位置
    let layerPositions: number[] = [];
    if (options.layered) {
        layerPositions = calculateLayerPositions(rootNode, options);
    }

    // 4. 第二次遍历 (Top-down) - 根据 _prelim 和 _mod 计算初步绝对坐标
    // 根节点的初始 modifier 为 -rootNode._prelim，尝试将其子树中心对齐到 0 轴
    // 初始 levelPos (levelStartX/Y) 为 0
    secondWalk(rootNode, -rootNode.rgTf.prelim, 0, options, 0, layerPositions);

    const {
        offset = { x: 0, y: 0 },
        align,
        isHorizontal,
    } = options;

    // 5. 处理对齐选项
    const alignmentOffset = calculateAlignmentOffset(rootNode, isHorizontal, align);

    // 6. 计算最终偏移量并应用
    const finalOffset = {
        x: offset.x + alignmentOffset.x,
        y: offset.y + alignmentOffset.y,
    };

    // 7. 计算最终偏移量并应用
    translateTree(rootNode, finalOffset.x, finalOffset.y);

    return rootNode;
}

// --- 初始化节点 ---
function initializeNodes(node: ITreeNodeForRT, parent: ITreeNodeForRT | null, depth: number): void {
    node.x = 0;
    node.y = 0;

    node.rgTf.prelim = 0;
    node.rgTf.mod = 0;
    node.rgTf.shift = 0;
    node.rgTf.change = 0;
    node.rgTf.thread = undefined;
    node.rgTf.ancestor = node; // 初始化时，祖先是自己
    node.rgTf.lmost_sibling = undefined;
    node.rgTf.number = parent ? parent.children.indexOf(node) : 0;
    node.rgTf.depth = depth;

    node.parent = parent || undefined;

    if (!node.collapsed) {
        node.children.forEach((child, index) => {
            initializeNodes(child, node, depth + 1);
            // 设置最左侧兄弟
            child.rgTf.lmost_sibling = index > 0 ? node.children[index - 1] : undefined;
        });
    }
}

// --- 第一次遍历 ---
function firstWalk(node: ITreeNodeForRT, options: LayoutOptions): void {

    // 如果节点折叠，则视其为叶节点处理，不处理其子节点
    const effectiveChildren = node.collapsed ? [] : node.children;

    if (effectiveChildren.length === 0) {
        // 叶节点或折叠节点：prelim 设为 0 (相对于父节点)
        // 如果有左兄弟，则 prelim 为左兄弟的 prelim + 间距
        if (node.rgTf.lmost_sibling) {
            const sibling = node.rgTf.lmost_sibling;
            const distance = getNodeDistance(sibling, node, options);
            node.rgTf.prelim = sibling.rgTf.prelim + distance;
        } else {
            node.rgTf.prelim = 0;
        }
        return;
    }

    // 非叶节点(且未折叠)：先递归处理子节点
    let defaultAncestor = effectiveChildren[0];
    effectiveChildren.forEach(child => {
        firstWalk(child, options);
        defaultAncestor = apportion(child, defaultAncestor, options);
    });

    // 应用计算出的 shift 和 change
    executeShifts(node); // executeShifts 内部也需要处理折叠情况

    // 计算父节点 prelim：通常是第一个和最后一个子节点 prelim 的中点
    const firstChild = effectiveChildren[0];
    const lastChild = effectiveChildren[effectiveChildren.length - 1];
    const midpoint = (firstChild.rgTf.prelim + lastChild.rgTf.prelim) / 2;

    // 如果有左兄弟，需要调整父节点的 prelim 以保持与兄弟的距离
    const leftSibling = node.rgTf.lmost_sibling;
    if (leftSibling) {
        const distance = getNodeDistance(leftSibling, node, options);
        node.rgTf.prelim = leftSibling.rgTf.prelim + distance;
        node.rgTf.mod = node.rgTf.prelim - midpoint; // 父节点的 mod 是其最终 prelim 与子节点中点的差值
    } else {
        // 如果没有左兄弟，父节点的 prelim 就是子节点的中点
        node.rgTf.prelim = midpoint;
    }
}

// --- 计算节点间距 (中心到中心) ---
function getNodeDistance(node1: ITreeNodeForRT, node2: ITreeNodeForRT, options: LayoutOptions): number {
    // 使用 Math.max(0, ...) 确保宽度/高度至少为 0，避免负值影响计算
    const n1Size = options.isHorizontal ? Math.max(0, node1.height) : Math.max(0, node1.width);
    const n2Size = options.isHorizontal ? Math.max(0, node2.height) : Math.max(0, node2.width);
    return (n1Size / 2) + (n2Size / 2) + options.siblingSeparation;
}

// --- 获取节点尺寸 (用于计算间距) ---
// 注意：此函数在当前代码中未被直接调用，getNodeDistance 直接计算尺寸。保留以供参考或未来使用。
// function getNodeSize(node: ITreeNodeForRT, options: LayoutOptions): number {
//     const size = options.isHorizontal ? node.height : node.width;
//     return Math.max(0, size); // 确保尺寸非负
// }

// --- 核心：冲突解决与子树分离 ---
function apportion(node: ITreeNodeForRT, defaultAncestor: ITreeNodeForRT, options: LayoutOptions): ITreeNodeForRT {
    const leftSibling = node.rgTf.lmost_sibling;
    if (leftSibling) {
        let innerRight: ITreeNodeForRT | undefined = node; // 当前子树的左轮廓节点
        let outerRight: ITreeNodeForRT | undefined = node; // 当前子树的右轮廓节点
        let innerLeft: ITreeNodeForRT | undefined = leftSibling; // 左兄弟子树的右轮廓节点
        let outerLeft: ITreeNodeForRT | undefined = node.parent?.children[0]; // 最左侧兄弟节点的左轮廓

        let sir = innerRight.rgTf.mod;
        let sor = outerRight.rgTf.mod;
        let sil = innerLeft.rgTf.mod;
        let sol = outerLeft?.rgTf.mod ?? 0;

        // 沿轮廓向下比较，直到到达轮廓末端
        while (nextRight(innerLeft) && nextLeft(innerRight)) {
            innerLeft = nextRight(innerLeft);
            innerRight = nextLeft(innerRight);
            outerLeft = nextLeft(outerLeft!); // outerLeft 总是最初最左边的子代
            outerRight = nextRight(outerRight);

            // 如果任何一个轮廓追踪指针失效，则停止（可能意味着树结构问题或到达末端）
            if (!innerLeft || !innerRight || !outerRight) break; // 如果树结构正确，就不会发生这种情况

            outerRight.rgTf.ancestor = node; // 链接祖先，以便以后进行移位传播

            const distance = getNodeDistance(innerLeft, innerRight, options);
            const shift = (innerLeft.rgTf.prelim + sil) - (innerRight.rgTf.prelim + sir) + distance;

            if (shift > 0) {
                // 需要移动右侧子树 (node)
                moveSubtree(ancestor(innerLeft, node, defaultAncestor), node, shift);
                sir = sir + shift; // 更新右侧累积偏移
                sor = sor + shift;
            }

            sil += innerLeft.rgTf.mod;
            sir += innerRight.rgTf.mod;
            sol += outerLeft!.rgTf.mod;
            sor += outerRight.rgTf.mod;
        }

        // 如果一个轮廓比另一个长，设置 thread 指针加速后续查找
        if (nextRight(innerLeft) && !nextLeft(outerRight)) {
            if (outerRight) {
                outerRight.rgTf.thread = nextRight(innerLeft);
                outerRight.rgTf.mod += sil - sor;
            }
        }
        if (nextLeft(innerRight) && !nextRight(outerLeft)) {
            if (outerLeft) {
                outerLeft.rgTf.thread = nextLeft(innerRight);
                outerLeft.rgTf.mod += sir - sol;
            }
            defaultAncestor = node; // 更新默认祖先
        }
    }
    return defaultAncestor;
}

// --- 移动子树 ---
function moveSubtree(wm: ITreeNodeForRT, wp: ITreeNodeForRT, shift: number): void {
    const subtrees = wp.rgTf.number - wm.rgTf.number; // 计算受影响的兄弟节点数量
    if (subtrees > 0 && wp.parent) { // 添加 wp.parent 检查
        const change = shift / subtrees;

        wp.rgTf.change -= change; // 应用到右侧（被移动的）子树根
        wp.rgTf.shift += shift; // 记录总位移

        wm.rgTf.change += change; // 应用到左侧（参照物）子树根
        wp.rgTf.prelim += shift; // 调整右子树根的 prelim
        wp.rgTf.mod += shift; // 调整右子树根的 mod
    }
}

// --- 应用累积的 shift 和 change ---
function executeShifts(node: ITreeNodeForRT): void {
    // 如果节点折叠，不执行其子节点的 shifts
    if (node.collapsed) return;

    let shift = 0;
    let change = 0;
    // 从后往前遍历子节点，累积 shift 和 change
    for (let i = node.children.length - 1; i >= 0; i--) {
        const child = node.children[i];
        child.rgTf.prelim += shift;
        child.rgTf.mod += shift;
        change += child.rgTf.change;
        shift += child.rgTf.shift + change; // 累积 shift 和 change
    }
}


// --- 获取轮廓上的下一个左节点 ---
function nextLeft(node: ITreeNodeForRT | undefined): ITreeNodeForRT | undefined {
    if (!node) return undefined;
    // 如果节点折叠，它就没有有效的子节点用于轮廓
    return !node.collapsed && node.children.length ? node.children[0] : node.rgTf.thread;
}

// --- 获取轮廓上的下一个右节点 ---
function nextRight(node: ITreeNodeForRT | undefined): ITreeNodeForRT | undefined {
    if (!node) return undefined;
    // 如果节点折叠，它就没有有效的子节点用于轮廓
    return !node.collapsed && node.children.length ? node.children[node.children.length - 1] : node.rgTf.thread;
}


// --- 查找祖先 ---
function ancestor(vil: ITreeNodeForRT, node: ITreeNodeForRT, defaultAncestor: ITreeNodeForRT): ITreeNodeForRT {
    // 检查 vil 的祖先是否是 node 的父节点的子节点 (即 vil 是否在 node 左侧兄弟的子树中)
    if (vil.rgTf.ancestor && node.parent?.children.includes(vil.rgTf.ancestor)) {
        return vil.rgTf.ancestor;
    }
    return defaultAncestor;
}


// --- 第二次遍历 (Top-down) ---
// modSum: 累积的修正值 (来自父节点和祖先节点)
// levelPos: 当前层级的起始位置 (水平布局是 Y 轴起始，垂直布局是 X 轴起始 - 但主要用于计算下一层位置)
function secondWalk(
    node: ITreeNodeForRT,
    modSum: number,
    levelPos: number,
    options: LayoutOptions,
    alignmentOffset: number = 0,
    layerPositions: number[] = []
): void {
    let x = 0;
    let y = 0;
    let nextLevelPos = 0; // 下一层级的起始位置
    let childAlignmentOffset = 0;

    if (options.isHorizontal) {
        // 水平布局 (Left-to-Right):
        // Y (垂直位置) 由 prelim 和 modSum 决定 (相对于兄弟节点和父节点的位置)
        y = node.rgTf.prelim + modSum;

        if (options.layered) {
            // 分层版本：使用预计算的层级位置
            x = layerPositions[node.rgTf.depth] || 0;
        } else {
            // 非分层版本：基于父节点位置计算
            // X (水平位置) 由层级深度和父节点位置/尺寸决定
            if (node.parent) {
                // 使用 Math.max 确保尺寸非负
                x = node.parent.x + Math.max(0, node.parent.width) + options.levelSeparation;
            } else {
                x = levelPos; // 根节点的初始 X (通常是 0)
            }
        }

        // 下一层级的起始 X 位置（仅非分层模式使用）
        if (!options.layered) {
            nextLevelPos = x + Math.max(0, node.width) + options.levelSeparation;
        }

        // 应用从父节点累积的垂直偏移
        y += alignmentOffset;

        // 计算本节点与子节点的中心对齐偏移
        if (node.children.length > 0 && !node.collapsed) {
            const nodeHeight = Math.max(0, node.height);
            const maxChildHeight = Math.max(...node.children.map(c => Math.max(0, c.height || 0)));
            childAlignmentOffset = (nodeHeight - maxChildHeight) / 2;
        }
    } else {
        // 垂直布局 (Top-to-Bottom):
        // X (水平位置) 由 prelim 和 modSum 决定 (相对于兄弟节点和父节点的位置)
        x = node.rgTf.prelim + modSum;

        if (options.layered) {
            // 分层版本：使用预计算的层级位置
            y = layerPositions[node.rgTf.depth] || 0;
        } else {
            // 非分层版本：基于父节点位置计算
            // Y (垂直位置) 由层级深度和父节点位置/尺寸决定
            if (node.parent) {
                // 使用 Math.max 确保尺寸非负
                y = node.parent.y + Math.max(0, node.parent.height) + options.levelSeparation;
            } else {
                y = levelPos; // 根节点的初始 Y (通常是 0)
            }
        }

        // 下一层级的起始 Y 位置（仅非分层模式使用）
        if (!options.layered) {
            nextLevelPos = y + Math.max(0, node.height) + options.levelSeparation;
        }

        // 应用从父节点累积的水平偏移
        x += alignmentOffset;

        // 计算本节点与子节点的水平中心对齐偏移
        if (node.children.length > 0 && !node.collapsed) {
            const nodeWidth = Math.max(0, node.width);
            const maxChildWidth = Math.max(...node.children.map(c => Math.max(0, c.width || 0)));
            childAlignmentOffset = (nodeWidth - maxChildWidth) / 2;
        }
    }

    // 设置的是初步的绝对坐标，后续可能需要平移
    node.x = x;
    node.y = y;

    // 如果节点未折叠，则递归处理其子节点
    if (!node.collapsed) {
        // 递归处理子节点，传递累积的 mod (modSum + node._mod) 和下一层级的起始位置
        // 注意：在原始 RT 算法中，levelPos 主要用于确定下一层的起始点，
        // 而子节点的 prelim + modSum 才是决定其主轴位置的关键。
        // 这里将 nextLevelPos 传递下去，但子节点的 x/y 计算主要还是依赖 parent 和 modSum。
        node.children.forEach(child => {
            secondWalk(
                child,
                modSum + node.rgTf.mod,
                nextLevelPos,
                options,
                alignmentOffset + childAlignmentOffset,
                layerPositions
            );
        });
    }
}

// --- (新增) 平移整个树 ---
function translateTree(node: ITreeNodeForRT, offsetX: number, offsetY: number): void {
    node.x += offsetX;
    node.y += offsetY;
    node.children.forEach(child => {
        translateTree(child, offsetX, offsetY);
    });
}

// --- 分层布局辅助函数 ---
/**
 * 预计算分层布局的层级位置
 * @param rootNode 根节点
 * @param options 布局选项
 * @returns 每层的起始位置数组
 */
function calculateLayerPositions(rootNode: ITreeNodeForRT, options: LayoutOptions): number[] {
    const layerMaxSizes = getLayerMaxSizes(rootNode, options.isHorizontal);
    const positions: number[] = [0]; // 第0层从0开始

    for (let i = 1; i < layerMaxSizes.length; i++) {
        const prevLayerSize = layerMaxSizes[i - 1];
        const prevPosition = positions[i - 1];
        // 每层位置 = 前一层位置 + 前一层最大节点尺寸 + 层级间距
        positions[i] = prevPosition + prevLayerSize + options.levelSeparation;
    }

    return positions;
}

/**
 * 获取每一层节点的最大尺寸
 * @param rootNode 根节点
 * @param isHorizontal 是否水平布局
 * @returns 每层的最大尺寸数组，索引对应层级深度
 */
function getLayerMaxSizes(rootNode: ITreeNodeForRT, isHorizontal: boolean): number[] {
    const layerSizes: number[] = [];

    traverseTreeRecursiveAdvanced<ITreeNodeForRT>(
        rootNode,
        (node) => {
            const depth = node.rgTf.depth;
            const size = isHorizontal
                ? Math.max(0, node.width || 0)
                : Math.max(0, node.height || 0);

            // 确保数组有足够的长度
            while (layerSizes.length <= depth) {
                layerSizes.push(0);
            }

            // 更新该层的最大尺寸
            if (size > layerSizes[depth]) {
                layerSizes[depth] = size;
            }

            // 如果节点已折叠，则通过返回 false 来停止遍历其子树
            if (node.collapsed) {
                return false;
            }
        },
        {
            order: 'pre',
            stopOnFalse: true,
        }
    );

    return layerSizes;
}